/**
 * logo-handler.ts - Logo upload and change functionality
 */

import { API_BASE_URL } from './constants';

/**
 * Trigger logo file picker for a tournament
 * @param tournamentId - Tournament ID
 */
export function changeTournamentLogo(tournamentId: string): void {
  const input = document.getElementById(`logo-input-${tournamentId}`) as HTMLInputElement | null;
  if (input) {
    input.click();
  }
}

/**
 * Handle logo file upload
 * @param tournamentId - Tournament ID
 * @param inputElement - File input element
 */
export async function handleLogoUpload(
  tournamentId: string,
  inputElement: HTMLInputElement
): Promise<void> {
  const file = inputElement.files?.[0];
  if (!file) return;

  // Validate file type
  if (!file.type.startsWith('image/')) {
    const errorMsg = window.i18n ? window.i18n.t('logo.selectImage') : 'Please select an image file';
    alert(errorMsg);
    return;
  }

  try {
    // Resize image to max 600x600 and convert to base64
    const base64 = await resizeAndConvertToBase64(file, 600);

    // Update tournament with new logo
    const response = await fetch(`${API_BASE_URL}/${tournamentId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ logo: base64 }),
    });

    if (response.ok) {
      // Update the logo image immediately
      const logoImg = document.getElementById(`tournament-logo-${tournamentId}`) as HTMLImageElement | null;
      if (logoImg) {
        logoImg.src = base64;
      }
    } else {
      const error = await response.json();
      const errorPrefix = window.i18n ? window.i18n.t('logo.uploadError') : 'Error updating logo';
      alert(`${errorPrefix}: ${error.error || 'Unknown error'}`);
    }
  } catch (error) {
    console.error('Error uploading logo:', error);
    const errorMsg = window.i18n ? window.i18n.t('logo.uploadFailed') : 'Failed to upload logo';
    alert(errorMsg);
  }

  // Clear the input
  inputElement.value = '';
}

/**
 * Resize image and convert to base64 data URL
 * @param file - Image file to resize
 * @param maxSize - Maximum width or height in pixels
 * @returns Promise resolving to base64 string
 */
function resizeAndConvertToBase64(file: File, maxSize: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.onload = () => {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;

        if (width > maxSize || height > maxSize) {
          if (width > height) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          } else {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }

        // Use better image smoothing for quality
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to base64 (JPEG for smaller size, 0.85 quality)
        const base64 = canvas.toDataURL('image/jpeg', 0.85);
        resolve(base64);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target?.result as string;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

// Export functions to window for onclick handlers
declare global {
  interface Window {
    changeTournamentLogo: typeof changeTournamentLogo;
    handleLogoUpload: typeof handleLogoUpload;
  }
}

window.changeTournamentLogo = changeTournamentLogo;
window.handleLogoUpload = handleLogoUpload;
